<template>
	<!-- Spinner while downloading -->
	<loader-generic v-if="showOverlaySpinner" isOverlay/>
	<!-- Toast for Download feedback -->
	<toast-generic v-if="showToast" :feedbackText="toastText" :toastType="toastType" @hideMe="showToast = false"/>
</template>

<script setup>
import LoaderGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
/* Vue & Assets */
import { ref, watch } from 'vue'
import webClient from '@/network/clients/webClientSetup'
import { mobileClientDownloadValidator } from '@/network/clients/mobileClientSetup'
import { fileDownloader } from '@/assets/scripts/fileDownloader'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['clearDownloadLink', 'clearDownloadLink'])

/* Props
@downloadLink {String} - link to download
@customSuccessMessage {String} - custom message for success toast
@fileName {String} hardcoded name for file
*/
const props = defineProps({
    downloadLink: String,
	customSuccessMessage: String,
	fileName: String
})

const showOverlaySpinner = ref(false)
const showToast = ref(false)
const toastText = ref('')
const toastType = ref('')

/* Triggering Download when downloadLink is populated */
watch(() => props.downloadLink, (newVal, oldVal) => {
	if (props.downloadLink) downloadHandler()
})

/* We need to check which platform we are in, for different download behaviours */
const downloadHandler = () => {
	if (window.cordova) {
		window.cordova.platformId === 'ios' ? downloadHandlerIOS() : downloadHandlerMobileApp()
	} else {
		downloadHandlerBrowser()
	}
}

/*************************************************
*												 *
*                     BROWSER                    *
*												 *
*************************************************/
const downloadHandlerBrowser = async () => {
	showOverlaySpinner.value = true
	try {
		await fileDownloader(props.downloadLink, props.fileName)
		downloaderSuccessHandler()
	} catch (error) {
		if (openFileNewTab(error)) return
		downloaderErrorHandler(error.message)
	}
}

const openFileNewTab = error => {
	if (error.message !== 'Network Error') return false
	showOverlaySpinner.value = false
	window.open(props.downloadLink, '_blank')
	emit('clearDownloadLink')
	return true
}

/*************************************************
*												 *
*                     ANDROID                    *
*												 *
*************************************************/
const downloadHandlerMobileApp = async () => {
	showOverlaySpinner.value = true

	try {
		const res = await webClient.ib({
			method: 'HEAD',
			url: props.downloadLink,
			responseType: 'arraybuffer',
			downloadMobileRequest: true
		})

		let fileName
		if (props.fileName) fileName = props.fileName
		else if (res.headers['content-disposition']) {
			const splitVariable = res.headers['content-disposition'].includes('fileName') ? 'fileName=' : 'filename='
			fileName = res.headers['content-disposition'].split(splitVariable)[1]
		} else fileName = 'download.pdf'

		window.cordova.plugins.DownloadManager.download(props.downloadLink, fileName, downloaderSuccessAndroidPreHandler, downloaderErrorHandler)
	} catch (error) {
		if (openFileNewTab(error)) return
		getReceiptErrorMessage()
	}
}

/* When we have an error running the Head method we need to do the same request but as a get so we can have MDW error */
const getReceiptErrorMessage = async () => {
	try {
		const res = await webClient.ib({
			method: 'GET',
			url: props.downloadLink,
			downloadMobileRequest: true
		})

		let fileName
		if (props.fileName) fileName = props.fileName
		else if (res.headers['content-disposition']) {
			const splitVariable = res.headers['content-disposition'].includes('fileName') ? 'fileName=' : 'filename='
			fileName = res.headers['content-disposition'].split(splitVariable)[1]
		} else fileName = 'download.pdf'

		window.cordova.plugins.DownloadManager.download(props.downloadLink, fileName, downloaderSuccessAndroidPreHandler, downloaderErrorHandler)
	} catch (error) {
		downloaderErrorHandler(error.response ? error.response.data.message : null)
	}
}
const downloaderSuccessAndroidPreHandler = () => {
	setTimeout(() => {
		downloaderSuccessHandler()
	}, 2000)
}

/*************************************************
*												 *
*                       iOS                      *
*												 *
*************************************************/
const downloadHandlerIOS = async () => {
	showOverlaySpinner.value = true

	try {
		const res = await mobileClientDownloadValidator({ method: 'GET', url: props.downloadLink })
		// window.cordova.InAppBrowser.open(props.downloadLink, '_system', 'location=yes,hidden=yes')
		console.log(res)
		let fileName
		if (props.fileName) fileName = props.fileName
		else if (res.headers['content-disposition']) {
			const splitVariable = res.headers['content-disposition'].includes('fileName') ? 'fileName=' : 'filename='
			fileName = res.headers['content-disposition'].split(splitVariable)[1]
		} else fileName = 'download.pdf'

		const filePath = window.cordova.file.dataDirectory + fileName
		iosDownloadingFile(filePath)
	} catch (error) {
		downloaderErrorHandler(error.response ? error.response.data.message : null)
	}
}

const iosDownloadingFile = async filePath => {
	try {
		await mobileClientDownloadValidator({ method: 'downloadFile', url: props.downloadLink, filePath: filePath })
		downloaderSuccessHandler()
		let fileExtension = 'application/pdf'

		if (filePath.includes('.xlsx')) fileExtension = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		else if (filePath.includes('.xls')) fileExtension = 'application/vnd.ms-excel'

		window.cordova.plugins.fileOpener2.open(
			filePath,
			fileExtension,
			{
				error: (e) => {
					console.log('Error status: ' + e.status + ' - Error message: ' + e.message)
				},
				success: () => {
					console.log('file opened successfully')
				}
			}
		)
	} catch (error) {
		downloaderErrorHandler(error.response ? error.response.data.message : null)
	}
}

/*************************************************
*             Success & Error Handlers           *
*************************************************/
/* Download Success Handler */
const downloaderSuccessHandler = () => {
	showOverlaySpinner.value = false
	showToast.value = true
	toastType.value = 'success'
	toastText.value = props.customSuccessMessage || t('genericDownloadReceiptSuccessMessage')
	emit('clearDownloadLink')
}
/* Download Error Handler */
const downloaderErrorHandler = errorMessage => {
	showOverlaySpinner.value = false
	showToast.value = true
	toastType.value = 'error'
	toastText.value = errorMessage
	emit('clearDownloadLink')
}
</script>

<style lang="scss">
</style>
